/**
 * ClientWhatOKRHubProvides Component
 * Section 5: What OKR Hub Provides - Execution discipline without micromanagement
 */

import React from 'react';
import { Target, Eye, BarChart3 } from 'lucide-react';

export const ClientWhatOKRHubProvides: React.FC = () => {
  const scrollToForm = () => {
    const element = document.getElementById('execution-fit-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const features = [
    {
      icon: Target,
      title: 'Execution structure',
      items: [
        'Clear objectives',
        'Measurable outcomes',
        'Defined execution cadence'
      ]
    },
    {
      icon: Eye,
      title: 'Visibility & governance',
      items: [
        'Progress rolled up across teams',
        'Leadership-ready execution views',
        'Early signals when priorities drift'
      ]
    },
    {
      icon: BarChart3,
      title: 'Sustained discipline',
      items: [
        'Regular check-ins without chasing',
        'Review-ready progress at any point',
        'Accountability built into the system'
      ]
    }
  ];

  return (
    <section className="bg-stone-50 py-20 md:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">

          {/* Headline */}
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-stone-900 mb-12 leading-tight">
            Execution discipline, without micromanagement.
          </h2>

          {/* Intro */}
          <div className="text-lg md:text-xl text-stone-700 leading-relaxed mb-12">
            <p>
              OKR Hub provides the structure needed to keep execution moving after alignment conversations end.
            </p>
          </div>

          {/* Features Grid */}
          <div className="space-y-8 mb-12">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white border-2 border-stone-200 rounded-xl p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-stone-900 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-display text-stone-900">{feature.title}</h3>
                  </div>
                  <ul className="space-y-3 ml-16">
                    {feature.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start text-lg text-stone-700">
                        <span className="text-stone-400 mr-3">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Closing Statement */}
          <div className="bg-stone-900 text-white rounded-xl p-8 mb-12">
            <p className="text-xl md:text-2xl leading-relaxed">
              This is not task or project management.
            </p>
            <p className="text-2xl md:text-3xl font-display mt-4">
              This is execution governance.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-12">
            <button
              onClick={scrollToForm}
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-stone-900 border-2 border-stone-300 rounded-lg hover:border-stone-900 hover:bg-white transition-all duration-300"
            >
              See if this level of execution discipline fits your clients
            </button>
          </div>

        </div>
      </div>
    </section>
  );
};
